/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2025, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Injectable } from '@angular/core';
import { AppConfigService } from '@app/common/app-config.service';
import { appConstants } from '@app/common/app-constants';
import { ProxyDataService } from '@app/core/http';
import { WFMDateTimeFormat } from '@app/di/data-model/date-time-format';
import { Logger } from '@core/lib/services';
import { TranslateService } from '@ngx-translate/core';

const log = new Logger('UvDatetimeService');
@Injectable({
  providedIn: 'root'
})
export class UvDatetimeService {
  wfmDateTimePref: WFMDateTimeFormat = <WFMDateTimeFormat>{};
  uvDateTimePref: any;

  constructor(
    private translate: TranslateService,
    private dataService: ProxyDataService,
    private appConfig: AppConfigService
  ) {}

  /**
   * getUVDateTimePref makes an HTTP GET request to retrieve the date-time preferences
   * If the preferences have changed, it prepares the new date-time formats and
   * updates the `AppConfigService` with the new preferences.
   *
   * In case of an error, it logs the issue and notifies the system status observer
   * with an appropriate error message.
   */
  getUVDateTimePref() {
    log.info('getUVDateTimePref() invoked');
    this.dataService.httpGet(appConstants.appURLs.getUVDateTimePref).then(
      response => {
        if (response.statusCode === 200) {
          // Clear any date time preference errors from system status area
          const categoryArray = response.categories;
          const dateTimePref = {};
          // Extract and fill the date time preference categories
          if (categoryArray) {
            categoryArray.forEach(categoryItem => {
              const DateFormatArray = categoryItem.Date;
              if (DateFormatArray) {
                DateFormatArray.forEach(Item => {
                  dateTimePref[Item.resourceKey] = Item.resourceValue;
                });
              }
              const TimeFormatArray = categoryItem.Time;
              if (TimeFormatArray) {
                TimeFormatArray.forEach(Item => {
                  dateTimePref[Item.resourceKey] = Item.resourceValue;
                });
              }
            });
          }
          if (JSON.stringify(dateTimePref) != JSON.stringify(this.uvDateTimePref)) {
            this.uvDateTimePref = dateTimePref;
            this.prepareDateTimeFormats();
            this.appConfig.setDateTimePref(this.wfmDateTimePref);
          }
        } else {
          log.warn(
            'UV date time preferences reading failed, Description :',
            response.statusDescription || response.uvError
          );
          this.appConfig.getSystemStatusNotifier().next({
            key: 'dateTimePref',
            message: this.translate.instant('dateTime.preference.error')
          });
        }
      },
      error => {
        log.error('Error occurred while getting UV date time preferences, Error :', error.statusText);
        this.appConfig.getSystemStatusNotifier().next({
          key: 'dateTimePref',
          message: this.translate.instant('dateTime.preference.error')
        });
      }
    );
  }

  getShortTimeFormat() {
    let timeDelimiter = this.uvDateTimePref.TIME_DELIMETER;
    if (timeDelimiter === appConstants.DelimiterSpace) {
      timeDelimiter = appConstants.SPACE;
    }
    let shortTimeFormat = 'hh' + timeDelimiter + 'mm A';
    if (this.uvDateTimePref.TIME_FORMAT === appConstants.TimeFormat.FORMAT_24H) {
      shortTimeFormat = 'HH' + timeDelimiter + 'mm';
    }
    return shortTimeFormat;
  }

  getLongTimeFormat() {
    let timeDelimiter = this.uvDateTimePref.TIME_DELIMETER;
    if (timeDelimiter === appConstants.DelimiterSpace) {
      timeDelimiter = appConstants.SPACE;
    }
    let longTimeFormat = 'hh' + timeDelimiter + 'mm' + timeDelimiter + 'ss A';
    if (this.uvDateTimePref.TIME_FORMAT === appConstants.TimeFormat.FORMAT_24H) {
      longTimeFormat = 'HH' + timeDelimiter + 'mm' + timeDelimiter + 'ss';
    }
    return longTimeFormat;
  }

  /**
   * Prepare the date and time formats based on the UV date time preferences.
   * This method constructs the date and time formats used throughout the application
   */
  prepareDateTimeFormats() {
    const dateStyle = this.uvDateTimePref.DATE_STYLE;
    let dateDelimiter = this.uvDateTimePref.DATE_DELIMETER;
    const dateFormat = this.uvDateTimePref.DATE_FORMAT;
    if (dateDelimiter === appConstants.DelimiterSpace) {
      dateDelimiter = appConstants.SPACE;
    }
    const isMonthInDigit = appConstants.locale.monthInDigits.indexOf(this.translate.currentLang) > -1;

    if (dateFormat === appConstants.DateFormat.dmy) {
      this.wfmDateTimePref.dateFormat = 'DD' + dateDelimiter + 'MMM' + dateDelimiter + 'YYYY';
      this.wfmDateTimePref.monthDayFormat = 'DD' + dateDelimiter + 'MMM';
      this.wfmDateTimePref.inputFormat = 'DD' + dateDelimiter + 'MM' + dateDelimiter + 'YYYY';
    } else if (dateFormat === appConstants.DateFormat.mdy) {
      this.wfmDateTimePref.dateFormat = 'MMM' + dateDelimiter + 'DD' + dateDelimiter + 'YYYY';
      this.wfmDateTimePref.monthDayFormat = 'MMM' + dateDelimiter + 'DD';
      this.wfmDateTimePref.inputFormat = 'MM' + dateDelimiter + 'DD' + dateDelimiter + 'YYYY';
    } else if (dateFormat === appConstants.DateFormat.ymd) {
      if (isMonthInDigit) {
        this.wfmDateTimePref.dateFormat = 'YYYY' + dateDelimiter + 'MM' + dateDelimiter + 'DD';
        this.wfmDateTimePref.monthDayFormat = 'MM' + dateDelimiter + 'DD';
      } else {
        this.wfmDateTimePref.dateFormat = 'YYYY' + dateDelimiter + 'MMM' + dateDelimiter + 'DD';
        this.wfmDateTimePref.monthDayFormat = 'MMM' + dateDelimiter + 'DD';
      }
      this.wfmDateTimePref.inputFormat = 'YYYY' + dateDelimiter + 'MM' + dateDelimiter + 'DD';
    }

    if (dateStyle === appConstants.DateStyle.LONG || dateStyle === appConstants.DateStyle.SHORT) {
      this.wfmDateTimePref.timeFormat = this.getShortTimeFormat();
    } else if (dateStyle === appConstants.DateStyle.FULL || dateStyle === appConstants.DateStyle.MEDIUM) {
      this.wfmDateTimePref.timeFormat = this.getLongTimeFormat();
    }

    this.wfmDateTimePref.dateDelimiter = dateDelimiter;
    this.wfmDateTimePref.dateTimeFormat = this.wfmDateTimePref.dateFormat + ' ' + this.wfmDateTimePref.timeFormat;
    this.wfmDateTimePref.timeDateFormat = this.wfmDateTimePref.timeFormat + ' ' + this.wfmDateTimePref.dateFormat;
    this.wfmDateTimePref.yearFormat = 'YYYY';
  }
}
